import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Clock, UserX, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";
import { siteConfig } from "@/config/siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  "General question",
  "Billing & subscriptions",
  "Bug report",
  "Feature request",
  "Privacy & data",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", topic: topics[0], message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", topic: topics[0], message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="support" className="border-t border-slate-200 bg-slate-50 py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-6xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Support</p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            Talk to a human.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
            Questions about plans, billing, the app or your data — send us a message
            and the ORIVA team will reply.
          </p>

          <div className="mt-9 flex flex-col gap-5">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 text-oriva-blue" />
              <div>
                <p className="text-sm font-medium text-slate-800">Email us directly</p>
                <a
                  href={`mailto:${siteConfig.contact.supportEmail}`}
                  data-testid="support-email-link"
                  className="text-sm text-oriva-blue underline-offset-4 hover:underline"
                >
                  {siteConfig.contact.supportEmail}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 text-oriva-blue" />
              <p className="text-sm text-slate-600">{siteConfig.contact.responseTime}</p>
            </div>
            <div className="flex items-start gap-3">
              <UserX className="mt-0.5 h-4 w-4 text-oriva-blue" />
              <p className="text-sm text-slate-600">
                Want your account and data removed? See the{" "}
                <Link
                  to="/account-deletion"
                  data-testid="support-deletion-link"
                  className="font-medium text-oriva-blue underline-offset-4 hover:underline"
                >
                  Account Deletion
                </Link>{" "}
                page.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-8"
            data-testid="contact-form"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-slate-800">
                  Name
                </label>
                <Input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-slate-800">
                  Email
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="mb-2 block text-sm font-medium text-slate-800">Topic</label>
              <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                <SelectTrigger data-testid="contact-topic-select">
                  <SelectValue placeholder="Choose a topic" />
                </SelectTrigger>
                <SelectContent>
                  {topics.map((topic) => (
                    <SelectItem key={topic} value={topic} data-testid={`contact-topic-${topic.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {topic}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-2 block text-sm font-medium text-slate-800">
                Message
              </label>
              <Textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?"
              />
            </div>

            <Button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-7 w-full rounded-full bg-oriva-blue py-6 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-700"
            >
              {sending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {sending ? "Sending…" : "Send message"}
            </Button>

            <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
              By sending this form you agree to our{" "}
              <Link to="/policies/privacy" className="underline underline-offset-2" data-testid="contact-privacy-link">
                Privacy Policy
              </Link>
              . We only use your details to respond to your enquiry.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
